import React, { useState, useEffect } from 'react';
import { FaUser, FaTrash, FaEye, FaSearch, FaUserSlash, FaSpinner, FaCopy, FaInfoCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { listUsers, getUserInfo, deleteUser, duplicateUserWithLowercase } from '../../services/usersApi';
import './ControlPanel.css';
import './UserManagement.css';
import Spinner from '../Spinner/Spinner';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [userDetails, setUserDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState({});
    const [duplicateLoading, setDuplicateLoading] = useState({});
    const [showCopyEmailsModal, setShowCopyEmailsModal] = useState(false);

    // Cargar la lista de usuarios al montar el componente
    useEffect(() => {
        fetchUsers();
    }, []);

    // Función para obtener la lista de usuarios
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const data = await listUsers();
            setUsers(data);
        } catch (error) {
            console.error('Error al cargar usuarios:', error);
            toast.error('No se pudieron cargar los usuarios');
        } finally {
            setLoading(false);
        }
    };

    // Función para obtener detalles de un usuario
    const handleViewDetails = async (email) => {
        setSelectedUser(email);
        setShowModal(true);
        setLoadingDetails(true);

        try {
            const details = await getUserInfo(email);
            setUserDetails(details);
        } catch (error) {
            console.error('Error al cargar detalles del usuario:', error);
            toast.error('No se pudieron cargar los detalles del usuario');
            setShowModal(false);
        } finally {
            setLoadingDetails(false);
        }
    };

    // Función para eliminar un usuario
    const handleDeleteUser = async (email) => {
        if (window.confirm(`¿Estás seguro de que deseas eliminar al usuario ${email}?`)) {
            setDeleteLoading(prev => ({ ...prev, [email]: true }));
            try {
                await deleteUser(email);
                toast.success('Usuario eliminado correctamente');
                // Actualizar la lista de usuarios
                setUsers(users.filter(user => user !== email));
            } catch (error) {
                console.error('Error al eliminar usuario:', error);
                toast.error('No se pudo eliminar el usuario');
            } finally {
                setDeleteLoading(prev => ({ ...prev, [email]: false }));
            }
        }
    };

    // Función para duplicar un usuario con email en minúsculas
    const handleDuplicateUserLowercase = async (email) => {
        if (email === email.toLowerCase()) {
            toast.warning('El email ya está en minúsculas');
            return;
        }

        if (window.confirm(`¿Estás seguro de que deseas duplicar el usuario ${email} con email en minúsculas?`)) {
            setDuplicateLoading(prev => ({ ...prev, [email]: true }));
            try {
                await duplicateUserWithLowercase(email);
                toast.success('Usuario duplicado correctamente');
                // Actualizar la lista de usuarios
                fetchUsers();
            } catch (error) {
                console.error('Error al duplicar usuario:', error);
                let errorMessage = 'No se pudo duplicar el usuario';

                if (error.response) {
                    // Mostrar mensaje del backend si está disponible
                    if (error.response.status === 409) {
                        errorMessage = 'Ya existe un usuario con este email en minúsculas';
                    } else if (error.response.data) {
                        errorMessage = error.response.data;
                    }
                }

                toast.error(errorMessage);
            } finally {
                setDuplicateLoading(prev => ({ ...prev, [email]: false }));
            }
        }
    };

    // Copiar emails al clipboard
    const handleCopyEmails = () => {
        if (filteredUsers.length === 0) {
            toast.warn("No hay usuarios para copiar.");
            return;
        }

        const emails = filteredUsers.join(', ');

        navigator.clipboard.writeText(emails).then(() => {
            toast.success("Emails copiados al portapapeles!");
            setShowCopyEmailsModal(false);
        }).catch(() => {
            toast.error("Error al copiar emails.");
        });
    };

    // Filtrar usuarios por término de búsqueda
    const filteredUsers = users.filter(email =>
        email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="control-panel-card">
            {loading && (<Spinner/>)}
            <div className="card-header">
                <FaUser /> Gestión de Usuarios
            </div>
            <div className="card-content">
                {/* Barra de búsqueda */}
                <div className="form-group" style={{ marginBottom: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                            <FaSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#aaa' }} />
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Buscar usuarios..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ paddingLeft: '35px' }}
                            />
                        </div>
                        {filteredUsers.length > 0 && (
                            <button
                                className="provider-btn user-mgmt-copy-emails-btn"
                                onClick={() => setShowCopyEmailsModal(true)}
                                style={{ marginLeft: '10px', backgroundColor: '#10b981', color: 'white' }}
                                title="Copiar emails"
                            >
                                <FaCopy />
                                Copiar Emails
                            </button>
                        )}
                        <button
                            className="provider-btn provider-btn-outline-primary"
                            onClick={fetchUsers}
                            style={{ marginLeft: '10px' }}
                        >
                            Actualizar
                        </button>
                    </div>
                </div>

                {/* Tabla de usuarios */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <FaSpinner className="fa-spin" style={{ fontSize: '2rem', color: '#ccc' }} />
                        <p>Cargando usuarios...</p>
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="providers-table">
                            <thead>
                            <tr>
                                <th style={{ width: '70%' }}>Email</th>
                                <th style={{ width: '30%' }}>Acciones</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredUsers.length > 0 ? (
                                filteredUsers.map((email) => (
                                    <tr key={email}>
                                        <td>{email}</td>
                                        <td>
                                            <div className="provider-action-buttons">
                                                <button
                                                    className="provider-action-btn"
                                                    onClick={() => handleViewDetails(email)}
                                                >
                                                    <FaEye />
                                                    <span className="provider-action-text">Ver</span>
                                                </button>
                                                {email !== email.toLowerCase() && (
                                                    <button
                                                        className="provider-action-btn"
                                                        style={{ backgroundColor: '#5bc0de' }}
                                                        onClick={() => handleDuplicateUserLowercase(email)}
                                                        disabled={duplicateLoading[email]}
                                                    >
                                                        {duplicateLoading[email] ? (
                                                            <FaSpinner className="fa-spin" />
                                                        ) : (
                                                            <FaCopy />
                                                        )}
                                                        <span className="provider-action-text">Duplicar</span>
                                                    </button>
                                                )}
                                                <button
                                                    className="provider-action-btn"
                                                    style={{ backgroundColor: '#d9534f' }}
                                                    onClick={() => handleDeleteUser(email)}
                                                    disabled={deleteLoading[email]}
                                                >
                                                    {deleteLoading[email] ? (
                                                        <FaSpinner className="fa-spin" />
                                                    ) : (
                                                        <FaTrash />
                                                    )}
                                                    <span className="provider-action-text">Eliminar</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="2" style={{ textAlign: 'center', padding: '20px' }}>
                                        <FaUserSlash style={{ fontSize: '1.5rem', color: '#aaa', marginBottom: '10px' }} />
                                        <p>No se encontraron usuarios</p>
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de detalles de usuario mejorado */}
            {showModal && (
                <div className="provider-modal-backdrop">
                    <div className="provider-modal-container user-mgmt-details-modal">
                        <div className="provider-modal-header">
                            <div className="user-mgmt-modal-header-content">
                                <FaUser className="user-mgmt-modal-header-icon" />
                                <div>
                                    <h3>Detalles del Usuario</h3>
                                    <span className="user-mgmt-modal-subtitle">{selectedUser}</span>
                                </div>
                            </div>
                            <button className="provider-modal-close" onClick={() => setShowModal(false)}>
                                ✕
                            </button>
                        </div>
                        <div className="provider-modal-body">
                            {loadingDetails ? (
                                <div className="user-mgmt-loading-container">
                                    <FaSpinner className="fa-spin user-mgmt-loading-spinner" />
                                    <p className="user-mgmt-loading-text">Cargando detalles del usuario...</p>
                                </div>
                            ) : userDetails ? (
                                <div className="user-mgmt-details-grid">
                                    {/* Información Personal */}
                                    <div className="user-mgmt-details-card">
                                        <div className="user-mgmt-card-header-small">
                                            <FaUser className="user-mgmt-card-icon" />
                                            <h4>Información Personal</h4>
                                        </div>
                                        <div className="user-mgmt-info-grid">
                                            <div className="user-mgmt-info-item">
                                                <span className="user-mgmt-info-label">Email:</span>
                                                <span className="user-mgmt-info-value">{userDetails.email}</span>
                                            </div>
                                            <div className="user-mgmt-info-item">
                                                <span className="user-mgmt-info-label">Nombre:</span>
                                                <span className="user-mgmt-info-value">{userDetails.name || 'No especificado'}</span>
                                            </div>
                                            <div className="user-mgmt-info-item">
                                                <span className="user-mgmt-info-label">Teléfono:</span>
                                                <span className="user-mgmt-info-value">{userDetails.phone || 'No especificado'}</span>
                                            </div>
                                            <div className="user-mgmt-info-item">
                                                <span className="user-mgmt-info-label">Ubicación:</span>
                                                <span className="user-mgmt-info-value">{userDetails.location || 'No especificada'}</span>
                                            </div>
                                            {userDetails.createdAt && (
                                                <div className="user-mgmt-info-item">
                                                    <span className="user-mgmt-info-label">Fecha de registro:</span>
                                                    <span className="user-mgmt-info-value">
                                                        {new Date(userDetails.createdAt).toLocaleDateString('es-ES', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Información de Proveedor (si aplica) */}
                                    {userDetails.isProvider && (
                                        <div className="user-mgmt-details-card user-mgmt-provider-card">
                                            <div className="user-mgmt-card-header-small">
                                                <FaUser className="user-mgmt-card-icon user-mgmt-provider-icon" />
                                                <h4>Información de Proveedor</h4>
                                                <span className="user-mgmt-provider-badge">Proveedor Activo</span>
                                            </div>
                                            <div className="user-mgmt-info-grid">
                                                <div className="user-mgmt-info-item">
                                                    <span className="user-mgmt-info-label">Tipo de Proveedor:</span>
                                                    <span className="user-mgmt-info-value user-mgmt-provider-type">
                                                        {userDetails.providerType === 'service' ? 'Servicios' : 'Productos'}
                                                    </span>
                                                </div>
                                                <div className="user-mgmt-info-item">
                                                    <span className="user-mgmt-info-label">Username de Proveedor:</span>
                                                    <span className="user-mgmt-info-value user-mgmt-provider-username">{userDetails.providerRef}</span>
                                                </div>
                                                <div className="user-mgmt-info-item">
                                                    <span className="user-mgmt-info-label">Estado del Proveedor:</span>
                                                    <span className="user-mgmt-info-value user-mgmt-status-badge user-mgmt-status-good">
                                                        Activo
                                                    </span>
                                                </div>
                                                {userDetails.providerCreatedAt && (
                                                    <div className="user-mgmt-info-item">
                                                        <span className="user-mgmt-info-label">Registrado como proveedor:</span>
                                                        <span className="user-mgmt-info-value">
                                                            {new Date(userDetails.providerCreatedAt).toLocaleDateString('es-ES', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric'
                                                            })}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Información Técnica (solo si es proveedor) */}
                                    {userDetails.isProvider && (
                                        <div className="user-mgmt-details-card">
                                            <div className="user-mgmt-card-header-small">
                                                <FaInfoCircle className="user-mgmt-card-icon" />
                                                <h4>Información Técnica del Proveedor</h4>
                                            </div>
                                            <div className="user-mgmt-info-grid">
                                                <div className="user-mgmt-info-item">
                                                    <span className="user-mgmt-info-label">Estado del Email:</span>
                                                    <span className={`user-mgmt-info-value user-mgmt-status-badge ${selectedUser === selectedUser.toLowerCase() ? 'user-mgmt-status-good' : 'user-mgmt-status-warning'}`}>
                                                        {selectedUser === selectedUser.toLowerCase() ? 'Formato correcto' : 'Contiene mayúsculas'}
                                                    </span>
                                                </div>
                                                <div className="user-mgmt-info-item">
                                                    <span className="user-mgmt-info-label">UID del Usuario:</span>
                                                    <span className="user-mgmt-info-value user-mgmt-uid-text">{userDetails.uid || 'No disponible'}</span>
                                                </div>
                                                <div className="user-mgmt-info-item">
                                                    <span className="user-mgmt-info-label">Referencia del Proveedor:</span>
                                                    <span className="user-mgmt-info-value user-mgmt-provider-username">{userDetails.providerRef}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="user-mgmt-error-container">
                                    <FaUserSlash className="user-mgmt-error-icon" />
                                    <p className="user-mgmt-error-text">No se pudieron cargar los detalles del usuario.</p>
                                </div>
                            )}
                        </div>
                        <div className="user-mgmt-modal-footer">
                            <div className="user-mgmt-modal-actions-left">
                                <button
                                    className="provider-btn provider-btn-outline"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cerrar
                                </button>
                            </div>
                            <div className="user-mgmt-modal-actions-right">
                                {selectedUser && selectedUser !== selectedUser.toLowerCase() && (
                                    <button
                                        className="provider-btn user-mgmt-btn-info"
                                        onClick={() => {
                                            setShowModal(false);
                                            handleDuplicateUserLowercase(selectedUser);
                                        }}
                                    >
                                        <FaCopy />
                                        <span className="provider-btn-text">Duplicar en minúsculas</span>
                                    </button>
                                )}
                                <button
                                    className="provider-btn user-mgmt-btn-danger"
                                    onClick={() => {
                                        setShowModal(false);
                                        handleDeleteUser(selectedUser);
                                    }}
                                >
                                    <FaTrash />
                                    <span className="provider-btn-text">Eliminar Usuario</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal para copiar emails */}
            {showCopyEmailsModal && (
                <div className="modal-overlay" onClick={() => setShowCopyEmailsModal(false)}>
                    <div className="modal-content user-mgmt-copy-emails-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="user-mgmt-modal-header">
                            <h3>📧 Emails de Usuarios</h3>
                            <button
                                className="user-mgmt-modal-close-btn"
                                onClick={() => setShowCopyEmailsModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="user-mgmt-modal-body">
                            <div className="user-mgmt-emails-count">
                                <span>📊</span>
                                <span>Total: {filteredUsers.length} usuarios{searchTerm && " (filtrados)"}</span>
                            </div>
                            <p>Los siguientes emails serán copiados al portapapeles:</p>
                            <div className="user-mgmt-emails-preview">
                                {filteredUsers.join(', ')}
                            </div>
                            <div className="user-mgmt-modal-actions">
                                <button
                                    className="user-mgmt-btn-secondary"
                                    onClick={() => setShowCopyEmailsModal(false)}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="user-mgmt-btn-primary"
                                    onClick={handleCopyEmails}
                                >
                                    <FaCopy size={16} />
                                    Copiar al Portapapeles
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;